import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ConclusionCard } from "@/components/m3/ConclusionCard";
import { DirectionCard } from "@/components/m3/DirectionCard";
import { EvidenceRefsHelper, formatQidRefs } from "@/lib/qidFormat";
import { VoiceInputButton, appendTranscript } from "@/components/VoiceInputButton";
import {
  Sparkles, MessageSquareQuote, ListChecks, Compass, RefreshCw,
  ArrowLeft, AlertCircle, ChevronRight,
} from "lucide-react";

const AI_SECTIONS = [
  { id: "experience_evidence", title: "Experience Evidence",
    intro: "What your stories demonstrate — draft, always yours to correct or reject." },
  { id: "strengths", title: "Demonstrated Strengths",
    intro: "Strengths tied to real examples you provided. Strengths with no supporting example are marked weaker evidence." },
  { id: "interests", title: "Interests",
    intro: "Motivators grounded in what you told us pulls you in — or drains you." },
  { id: "work_style", title: "Preferred Work Style",
    intro: "How you seem to prefer to work, inferred from your answers. Draft." },
];

const DET_SECTIONS = [
  { id: "learning_preferences", title: "Learning Preferences",
    icon: ListChecks,
    intro: "Directly from what you chose in Section 5. No AI involved." },
  { id: "digital_confidence", title: "Digital & AI Starting Level",
    icon: Sparkles,
    intro: "Deterministic — computed from your Section 6 answers. Starting point, not an intelligence score." },
  { id: "constraints", title: "Real-life Constraints",
    icon: MessageSquareQuote,
    intro: "Directly from your Section 1 answers about schedule, income, care, and access." },
  { id: "uncertainties", title: "Uncertainties",
    icon: AlertCircle,
    intro: "Honest gaps: skipped questions and strengths selected without examples." },
];

function groupConclusions(profile) {
  const out = {
    experience_evidence: [], strengths: [], interests: [], work_style: [],
    learning_preferences: [], digital_confidence: [], constraints: [], uncertainties: [],
  };
  for (const c of profile?.conclusions || []) {
    if (out[c.section]) out[c.section].push(c);
  }
  return out;
}

export default function Results() {
  const [defs, setDefs] = useState(null);
  const [state, setState] = useState(null);
  const [profile, setProfile] = useState(null);
  const [dirsData, setDirsData] = useState(null); // {direction_sets, actions, reconsideration_count, cap, remaining}
  const [briefing, setBriefing] = useState(null); // {analysis_generated_at_short, edited_after_submit, coverage_notice, answered_count_at_generation, ...}
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [genBusy, setGenBusy] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const [reconsiderOpen, setReconsiderOpen] = useState(false);
  const [reconsiderFeedback, setReconsiderFeedback] = useState("");
  const [reconsiderBusy, setReconsiderBusy] = useState(false);
  const [genError, setGenError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const [d, s, p, dr, br] = await Promise.all([
        api.get("/assessment/definitions"),
        api.get("/assessment/state"),
        api.get("/profile").catch(() => ({ data: { profile: null } })),
        api.get("/directions").catch(() => ({ data: null })),
        // M1/M4 (fix cycle 2026-09-25): briefing exposes stored
        // analysis dates + the edited_after_submit pill trigger.
        // Failure here never blocks Results — pill just won't render.
        api.get("/briefing").catch(() => ({ data: null })),
      ]);
      setDefs(d.data);
      setState(s.data);
      setProfile(p.data?.profile || null);
      setDirsData(dr.data || null);
      setBriefing(br.data || null);
    } catch (err) {
      setError(extractApiError(err, "Failed to load your results."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submitted = !!state?.progress?.submitted_at;
  const groups = useMemo(() => groupConclusions(profile), [profile]);
  const questionMap = useMemo(() => {
    const m = {};
    for (const q of defs?.questions || []) m[q.id] = q;
    return m;
  }, [defs]);

  const generateProfile = async () => {
    setGenBusy(true);
    setGenError(null);
    try {
      const { data } = await api.post("/profile/generate");
      setProfile(data);
      toast.success("Your draft profile is ready.");
    } catch (err) {
      setGenError(extractApiError(err, "Generation failed."));
    } finally {
      setGenBusy(false);
    }
  };

  const generateDirections = async (feedback = null) => {
    setReconsiderBusy(true);
    setGenError(null);
    try {
      const { data } = await api.post("/directions/generate", { feedback });
      // /directions/generate returns the new set + counters; refresh list
      const dr = await api.get("/directions");
      setDirsData(dr.data);
      toast.success(feedback ? "Reconsidered Lanes of Direction ready." : "Your 3 Lanes of Direction are ready.");
      setReconsiderOpen(false);
      setReconsiderFeedback("");
      return data;
    } catch (err) {
      setGenError(extractApiError(err, "Generation failed."));
    } finally {
      setReconsiderBusy(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div data-testid="results-loading" className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary">
          Loading your results…
        </div>
      </AppShell>
    );
  }

  if (error) {
    return (
      <AppShell>
        <div className="mx-auto max-w-3xl px-4 py-14">
          <Alert variant="destructive" data-testid="results-error"><AlertDescription>{error}</AlertDescription></Alert>
        </div>
      </AppShell>
    );
  }

  if (!submitted) {
    return (
      <AppShell>
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-14">
          <div className="rounded-lg border border-border-subtle bg-bg-surface p-8" data-testid="results-not-submitted">
            <h1 className="text-3xl font-heading font-medium text-fg-primary">Not ready yet</h1>
            <p className="mt-3 text-sm text-fg-secondary">
              Your results workspace opens after you submit your Career Repath
              Assessment. You can save and edit as you go.
            </p>
            <div className="mt-5 flex gap-3">
              <Link to="/assessment"><Button data-testid="results-open-assessment-btn" className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover">Open the assessment</Button></Link>
              <Link to="/dashboard"><Button variant="ghost" className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover" data-testid="results-back-dashboard-btn"><ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />Dashboard</Button></Link>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  const answersById = state?.answers || {};
  const currentSet = dirsData?.direction_sets?.[0] || null; // sorted version desc
  const priorSets = (dirsData?.direction_sets || []).slice(1);
  const actions = dirsData?.actions || [];
  const selectedActionOnCurrent = currentSet
    ? actions.find((a) => a.set_id === currentSet.id && a.action === "select")
    : null;
  // ensure the most-recent select for THIS set hasn't been un-selected
  const currentSetSelectId = (() => {
    if (!currentSet) return null;
    const rel = actions
      .filter((a) => a.set_id === currentSet.id && (a.action === "select" || a.action === "unselect"))
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    if (rel.length && rel[0].action === "select") return rel[0].direction_id;
    return null;
  })();

  const rejectedInGroups = Object.values(groups).flat().some((c) => c.status === "rejected");

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-fg-secondary">Results workspace</p>
            <h1 className="mt-1 text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary" data-testid="results-title">
              Your 3 Lanes of Direction
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-fg-secondary">
              Everything below is a draft <span className="text-fg-primary">you</span> own.
              AI-generated conclusions can be corrected or rejected. Deterministic
              sections come straight from what you chose in the assessment.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {rejectedInGroups && (
              <label className="flex items-center gap-2 text-xs text-fg-secondary" data-testid="show-rejected-toggle-row">
                <Switch
                  checked={showRejected}
                  onCheckedChange={setShowRejected}
                  aria-label="Show rejected conclusions"
                  data-testid="show-rejected-toggle"
                />
                Show rejected conclusions
              </label>
            )}
            <Link to="/assessment/review">
              <Button variant="ghost" size="sm" data-testid="results-edit-answers-btn" className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
                <ArrowLeft className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                Edit my answers
              </Button>
            </Link>
          </div>
        </div>

        {/* Anchor nav */}
        <nav aria-label="Results sections" className="mb-8 rounded-lg border border-border-subtle bg-bg-surface p-3 flex flex-wrap gap-1 text-sm" data-testid="results-anchor-nav">
          {[
            { href: "#what-we-heard", label: "What we heard", tid: "anchor-what-we-heard" },
            { href: "#profile", label: "Your profile", tid: "anchor-profile" },
            { href: "#uncertainties", label: "Uncertainties", tid: "anchor-uncertainties" },
            { href: "#directions", label: "Three Directions", tid: "anchor-directions" },
          ].map((a) => (
            <a key={a.href} href={a.href} data-testid={a.tid} className="px-3 py-1.5 rounded-md text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover transition-colors">
              {a.label}
            </a>
          ))}
        </nav>

        {/* What we heard */}
        <section id="what-we-heard" className="scroll-mt-24 mb-10">
          <h2 className="text-2xl font-heading font-medium text-fg-primary">What we heard</h2>
          <p className="mt-1 text-xs text-fg-secondary">A short mirror of what you told us. Deterministic — no AI involved.</p>
          <div className="mt-3 rounded-lg border border-border-subtle bg-bg-surface p-5 text-sm text-fg-secondary whitespace-pre-line" data-testid="what-we-heard">
            {formatQidRefs(profile?.what_we_heard) || "Generate your profile to see this summary."}
          </div>
          {/* Founder-pass Finding 1: helper is shown once, above the
              first evidence-linked explanation area on this page. */}
          {profile && (
            <div className="mt-4">
              <EvidenceRefsHelper testid="results-evidence-refs-helper" />
            </div>
          )}
        </section>

        {/* Profile */}
        <section id="profile" className="scroll-mt-24 mb-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-2xl font-heading font-medium text-fg-primary">Your profile</h2>
            {!profile ? (
              <Button
                data-testid="generate-profile-btn"
                onClick={generateProfile}
                disabled={genBusy}
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                <Sparkles className="h-4 w-4 mr-2" aria-hidden="true" />
                {genBusy ? "Generating your draft…" : "Generate my draft profile"}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={generateProfile}
                disabled={genBusy}
                data-testid="regenerate-profile-btn"
                className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                {genBusy ? "Regenerating…" : "Regenerate profile"}
              </Button>
            )}
          </div>
          {genBusy && (
            <div
              role="status"
              aria-live="polite"
              data-testid="generation-waiting-banner"
              className="mt-4 rounded-lg border border-accent-warm bg-accent-warm/5 p-4"
            >
              <div className="flex items-start gap-3">
                <RefreshCw className="h-5 w-5 mt-0.5 animate-spin text-accent-warm" aria-hidden="true" />
                <div className="text-sm text-fg-primary">
                  <p className="font-medium">Working on your profile now.</p>
                  <p className="mt-1 text-fg-secondary">
                    Your answers are saved. This usually takes one to two minutes.
                    You can leave this page — if it finishes while you're away,
                    your results will be here waiting when you return.
                  </p>
                  <p className="mt-1 text-xs text-fg-muted italic">
                    Refreshing won't start a second run — the same request is protected server-side.
                  </p>
                </div>
              </div>
            </div>
          )}
          {genError && (
            <Alert variant="destructive" className="mt-3" data-testid="results-gen-error">
              <AlertDescription>{genError}</AlertDescription>
            </Alert>
          )}

          {!profile && !genBusy && (
            <p className="mt-3 text-sm text-fg-secondary" data-testid="profile-empty-hint">
              When you generate, we send your answers to the configured AI provider once and
              produce evidence-linked drafts. Nothing here is a promise or a decision.
            </p>
          )}

          {profile && (
            <div className="mt-6 space-y-8">
              {/* AI sections */}
              {AI_SECTIONS.map((sec) => {
                const items = (groups[sec.id] || []).filter((c) => showRejected || c.status !== "rejected");
                return (
                  <div key={sec.id} data-testid={`profile-section-${sec.id}`}>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs uppercase tracking-wider text-accent-warm">AI draft</span>
                      <h3 className="text-xl font-heading font-medium text-fg-primary">{sec.title}</h3>
                    </div>
                    <p className="mt-1 text-xs text-fg-secondary">{sec.intro}</p>
                    <div className="mt-3 space-y-3">
                      {items.length === 0 ? (
                        <p className="text-sm text-fg-muted italic" data-testid={`profile-section-${sec.id}-empty`}>
                          Nothing here yet.
                        </p>
                      ) : items.map((c) => (
                        <ConclusionCard
                          key={c.id} c={c}
                          answersById={answersById} questionMap={questionMap}
                          onChanged={(p) => setProfile(p)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Deterministic sections (learning + digital + constraints; uncertainties gets its own anchor below) */}
              {DET_SECTIONS.filter((s) => s.id !== "uncertainties").map((sec) => {
                const items = groups[sec.id] || [];
                const Icon = sec.icon;
                return (
                  <div key={sec.id} data-testid={`profile-section-${sec.id}`}>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs uppercase tracking-wider text-progress-teal">Deterministic — from your answers</span>
                      <h3 className="text-xl font-heading font-medium text-fg-primary flex items-center gap-2">
                        <Icon className="h-4 w-4" aria-hidden="true" />
                        {sec.title}
                      </h3>
                    </div>
                    <p className="mt-1 text-xs text-fg-secondary">{sec.intro}</p>
                    <div className="mt-3 space-y-3">
                      {items.length === 0 ? (
                        <p className="text-sm text-fg-muted italic" data-testid={`profile-section-${sec.id}-empty`}>
                          Nothing recorded for this section yet.
                        </p>
                      ) : items.map((c) => (
                        <ConclusionCard
                          key={c.id} c={c}
                          answersById={answersById} questionMap={questionMap}
                          onChanged={(p) => setProfile(p)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Uncertainties (its own anchor) */}
        <section id="uncertainties" className="scroll-mt-24 mb-10">
          <div className="flex items-baseline gap-2">
            <span className="text-xs uppercase tracking-wider text-progress-teal">Deterministic</span>
            <h2 className="text-2xl font-heading font-medium text-fg-primary flex items-center gap-2">
              <AlertCircle className="h-5 w-5" aria-hidden="true" />
              Uncertainties
            </h2>
          </div>
          <p className="mt-1 text-xs text-fg-secondary">{DET_SECTIONS.find(s => s.id === "uncertainties").intro}</p>
          <div className="mt-3 space-y-3" data-testid="profile-section-uncertainties">
            {(groups.uncertainties || []).length === 0 ? (
              <p className="text-sm text-fg-muted italic">Generate your profile to see gaps and skipped items.</p>
            ) : groups.uncertainties.map((c) => (
              <ConclusionCard
                key={c.id} c={c}
                answersById={answersById} questionMap={questionMap}
                onChanged={(p) => setProfile(p)}
              />
            ))}
          </div>
        </section>

        {/* Three Directions */}
        <section id="directions" className="scroll-mt-24 mb-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-2xl font-heading font-medium text-fg-primary flex items-center gap-2">
                <Compass className="h-5 w-5" aria-hidden="true" />
                Three Directions
              </h2>
              <p className="mt-1 text-xs text-fg-secondary">
                Three possible directions to explore, grounded in your answers. You control the pace — selecting one doesn't lock anything, and you can change your mind. Your answers stay saved.
              </p>
              {reconsiderBusy && (
                <div
                  role="status"
                  aria-live="polite"
                  data-testid="directions-waiting-banner"
                  className="mt-3 rounded-lg border border-accent-warm bg-accent-warm/5 p-3 max-w-xl"
                >
                  <div className="flex items-start gap-3">
                    <RefreshCw className="h-4 w-4 mt-0.5 animate-spin text-accent-warm" aria-hidden="true" />
                    <p className="text-xs text-fg-primary">
                      Working on your 3 Lanes of Direction. This usually takes one to two minutes.
                      Your answers are saved; refreshing won't start a second run.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              {!currentSet ? (
                <Button
                  onClick={() => generateDirections(null)}
                  disabled={reconsiderBusy}
                  data-testid="generate-directions-btn"
                  className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                >
                  <Sparkles className="h-4 w-4 mr-2" aria-hidden="true" />
                  {reconsiderBusy ? "Generating…" : "Generate my 3 Lanes of Direction"}
                </Button>
              ) : (
                <>
                  <p className="text-xs text-fg-secondary" data-testid="reconsideration-counter">
                    Reconsiderations used: <span className="text-fg-primary">{dirsData?.reconsideration_count || 0}</span> / {dirsData?.reconsideration_cap || 0}
                    {" · "}
                    <span className="text-fg-primary">{dirsData?.reconsiderations_remaining ?? 0} left</span>
                  </p>
                  {(dirsData?.reconsiderations_remaining ?? 0) > 0 ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setReconsiderOpen((v) => !v)}
                      disabled={reconsiderBusy}
                      data-testid="reconsider-toggle-btn"
                      className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                      {reconsiderOpen ? "Cancel reconsider" : "Ask for a reconsideration"}
                    </Button>
                  ) : (
                    <p className="text-xs text-fg-muted italic" data-testid="reconsideration-exhausted">
                      Reconsiderations exhausted. Your previous sets are still saved below.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {genError && (
            <Alert variant="destructive" className="mt-3" data-testid="directions-gen-error">
              <AlertDescription>{genError}</AlertDescription>
            </Alert>
          )}

          {/* Reconsideration form */}
          {reconsiderOpen && (
            <div className="mt-4 rounded-lg border border-border-subtle bg-bg-surface p-5" data-testid="reconsider-form">
              <label className="text-sm text-fg-primary">
                What didn't fit about the current three? <span className="text-fg-muted">(optional; helps the model)</span>
              </label>
              <Textarea
                data-testid="reconsider-feedback-input"
                placeholder="e.g., None of them respected my evening-caregiver constraint…"
                maxLength={800}
                value={reconsiderFeedback}
                onChange={(e) => setReconsiderFeedback(e.target.value)}
                className="mt-2 bg-bg-base border-border-subtle text-fg-primary"
              />
              <div className="mt-2">
                <VoiceInputButton
                  fieldTestid="reconsider-feedback-voice-btn"
                  ariaLabel="Speak your reconsideration feedback"
                  onTranscript={(t) =>
                    setReconsiderFeedback(appendTranscript(reconsiderFeedback, t))}
                />
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  onClick={() => generateDirections(reconsiderFeedback.trim() || null)}
                  disabled={reconsiderBusy}
                  data-testid="reconsider-submit-btn"
                  className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                >
                  {reconsiderBusy ? "Reconsidering…" : "Generate reconsidered set"}
                </Button>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => { setReconsiderOpen(false); setReconsiderFeedback(""); }}
                  data-testid="reconsider-cancel-btn"
                  className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
                >Cancel</Button>
              </div>
            </div>
          )}

          {currentSet && (
            <>
              <div className="mt-6 flex items-baseline justify-between">
                <p className="text-xs uppercase tracking-wider text-accent-warm">Current set — v{currentSet.version}{currentSet.is_reconsideration ? " · reconsidered" : ""}</p>
                <p className="text-xs text-fg-muted" data-testid="results-analysis-as-of">
                  Based on your answers as of {new Date(currentSet.created_at).toLocaleDateString()}
                </p>
              </div>
              {/* M4 (fix cycle 2026-09-25, Mark): honest "answers changed
                  since analysis" pill. Trigger:
                    - progress.edited_after_submit === true, OR
                    - current answered count diverges from the set's
                      snapshot (`answered_count_at_generation`).
                  Legacy sets that predate the additive field fall
                  back to the edited_after_submit flag alone. VIEWING
                  never consumes a reconsideration allowance —  the
                  numbers here are read from `dirsData` which is a
                  read-only summary. Consumption only happens when
                  the user actually submits the Reconsider form. */}
              {(() => {
                const answeredNow =
                  Object.values(state?.answers || {}).filter(
                    (v) => v && v.answer != null && v.answer !== "" &&
                           !(Array.isArray(v.answer) && v.answer.length === 0) &&
                           !v.skipped,
                  ).length;
                const snapshotCount = currentSet?.answered_count_at_generation;
                const editedFlag = !!briefing?.edited_after_submit ||
                                   !!state?.progress?.edited_after_submit;
                const divergent = Number.isFinite(snapshotCount) &&
                                  snapshotCount !== answeredNow;
                const shouldShowPill = editedFlag || divergent;
                if (!shouldShowPill) return null;
                const cap = dirsData?.reconsideration_cap || 0;
                const used = dirsData?.reconsideration_count || 0;
                const remaining = dirsData?.reconsiderations_remaining ?? null;
                const capNumeric = Number.isFinite(cap) && cap > 0;
                return (
                  <div
                    role="status"
                    data-testid="results-edited-since-analysis-pill"
                    className="mt-3 rounded-md border border-accent-warm/50 bg-accent-warm/10 p-3 flex flex-col sm:flex-row items-start sm:items-center gap-3"
                  >
                    <div className="flex items-start gap-2 flex-1">
                      <AlertCircle className="h-4 w-4 mt-0.5 text-accent-warm flex-shrink-0" aria-hidden="true" />
                      <div className="text-xs text-fg-primary">
                        <p className="font-medium">
                          You've edited answers since this analysis.
                        </p>
                        <p className="mt-1 text-fg-secondary">
                          Your previous direction set stays saved. Reconsider to
                          refresh — nothing regenerates on its own.
                          {capNumeric ? (
                            <> Reconsiderations used: <span className="text-fg-primary">{used}</span>/{cap}
                              {remaining !== null && <> · <span className="text-fg-primary">{remaining} left</span></>}.
                            </>
                          ) : null}
                        </p>
                      </div>
                    </div>
                    {capNumeric && (remaining ?? 0) > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setReconsiderOpen(true);
                          // Scroll form into view; the modal is inline.
                          setTimeout(() => {
                            const el = document.querySelector('[data-testid="reconsider-form"]');
                            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                          }, 50);
                        }}
                        data-testid="results-edited-pill-reconsider-btn"
                        className="border-accent-warm/50 text-accent-warm hover:bg-accent-warm/10 flex-shrink-0"
                      >
                        <RefreshCw className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                        Reconsider now
                      </Button>
                    )}
                  </div>
                );
              })()}
              {/* T2 (fix cycle 2026-09-25, Mark): "based on limited
                  information" honest coverage notice for the 15%-50%
                  band. Sourced from the direction_set's stored
                  `coverage_notice`; older sets don't carry it → no
                  notice. Persistent (not a toast) so it stays visible
                  while the user reads the directions. */}
              {currentSet.coverage_notice && currentSet.coverage_notice.active && (
                <div
                  data-testid="results-coverage-notice"
                  role="status"
                  className="mt-3 rounded-md border border-accent-warm/50 bg-accent-warm/10 p-3"
                >
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 text-accent-warm flex-shrink-0" aria-hidden="true" />
                    <div className="text-xs text-fg-primary">
                      <p className="font-medium">Based on limited information.</p>
                      <p className="mt-1 text-fg-secondary">
                        {currentSet.coverage_notice.message}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* Choices-to-explore banner — locks equal visual weight (Mark 2026-09-23c). */}
              <div
                data-testid="directions-equal-weight-banner"
                className="mt-3 rounded-md border border-border-subtle bg-bg-base p-3 text-xs text-fg-secondary"
              >
                These are <span className="text-fg-primary">three choices to explore</span> — not ranked winners.
                Numbering is a label, not an order. You can explore one, change your mind, and pick a different direction later. Your answers stay saved.
              </div>
              <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="directions-grid">
                {currentSet.directions.map((d, i) => (
                  <DirectionCard
                    key={d.id} direction={d} setId={currentSet.id}
                    isCurrentSet={true}
                    positionNumber={i + 1}
                    actions={actions}
                    answersById={answersById} questionMap={questionMap}
                    selectedOtherId={currentSetSelectId && currentSetSelectId !== d.id ? currentSetSelectId : null}
                    onUpdate={(data) => setDirsData(data)}
                  />
                ))}
              </div>
            </>
          )}

          {priorSets.length > 0 && (
            <div className="mt-8" data-testid="prior-direction-sets">
              <h3 className="text-lg font-heading font-medium text-fg-primary">Earlier sets (for comparison)</h3>
              <p className="mt-1 text-xs text-fg-secondary">Your previous direction sets are kept — you can compare them side-by-side.</p>
              <div className="mt-3 space-y-6">
                {priorSets.map((s) => (
                  <div key={s.id} data-testid={`direction-set-${s.id}`}>
                    <div className="flex items-baseline justify-between">
                      <p className="text-xs uppercase tracking-wider text-fg-secondary">
                        v{s.version}{s.is_reconsideration ? " · reconsidered" : ""}
                      </p>
                      <p className="text-xs text-fg-muted">
                        Based on your answers as of {new Date(s.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {s.reconsideration_feedback && (
                      <p className="mt-1 text-xs text-fg-muted italic">
                        Your feedback: “{s.reconsideration_feedback}”
                      </p>
                    )}
                    <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {s.directions.map((d, i) => (
                        <DirectionCard
                          key={d.id} direction={d} setId={s.id}
                          isCurrentSet={false}
                          positionNumber={i + 1}
                          actions={actions}
                          answersById={answersById} questionMap={questionMap}
                          selectedOtherId={null}
                          onUpdate={(data) => setDirsData(data)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <div className="mt-6 flex items-center justify-between text-sm">
          <Link to="/dashboard">
            <Button variant="ghost" data-testid="results-back-dashboard-bottom" className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
              <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
              Back to dashboard
            </Button>
          </Link>
          <Link to="/settings" className="text-fg-secondary hover:text-fg-primary transition-colors" data-testid="results-settings-link">
            Manage or export your data <ChevronRight className="h-3 w-3 inline" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </AppShell>
  );
}
